/**
 * R11 step 1 (ARCHITECTURE.md §5.5, "R11, the design"): the input envelope's dispatcher.
 *
 * `Match.apply(input)` checks the input's stamp, hands its body here, and logs the result.
 * This module maps each body onto the Match method that already does the work, so applying
 * an input and calling the method are the same thing. That is what makes step 1 move no
 * result. The hosts still call the methods directly; step 2 moves them onto `apply`.
 *
 * What is not here yet:
 * - turn-order checks (whose turn it is): the hosts make them today (`canApplyNetAction`);
 *   they join `apply` with step 2;
 * - (lane C's intent and possession rules landed with 64.10: who holds a unit is `state.controllers`;
 *   an intent from a seat that does not hold the unit is refused; intents on the grid, a turn an impulse);
 * - the turn flow's own steps (`skip`, the P2P `flightEnd` marker) and the harness's
 *   bookkeeping actions (`capture`, `buildDrill`): host-driven until step 2;
 * - orders and the fidelity switch: refused until the Match holds squads (step 2) and a
 *   surrogate exists (62.9);
 * - brains' decisions: step 6.
 */
import type {
  CollectActionParams, CommandParams, FireParams, JetpackParams, MatchInputBody, MoveParams, ShopParams, TurnAction, TurnInput,
  BuildParams, MountParams, HealParams, MeleeParams, PlantFlagParams, RemoveStakeParams, PickupParams, DropParams, RuinParams, DetonateParams,
} from '../types';
import type { Match, PlanResult, SimPresenter } from './match';
import { seatAt, seatUnit } from '../core/seats';

/** A human seat's actor id: negative, so it never collides with a unit id (a brain's actor id). */
export const seatActor = (team: number): number => -1 - team;
/** The team a human seat's actor id stands for, or null for a brain. */
export const seatTeam = (actor: number): number | null => actor < 0 ? -1 - actor : null;

/** Where each turn action is applied in step 1: by the Match, or still by the host's flow (step 2). */
export const TURN_ACTION_ROUTES: Record<TurnAction, 'match' | 'host'> = {
  fire: 'match', queue: 'match', unqueue: 'match', jump: 'match', jetpack: 'match', move: 'match', shop: 'match',
  build: 'match', mount: 'match', dismount: 'match', heal: 'match', melee: 'match', plantFlag: 'match',
  removeStake: 'match', pickup: 'match', drop: 'match', interact: 'match', claim: 'match', collect: 'match',
  deliver: 'match', command: 'match', detonate: 'match', skip: 'match',   // skip: the end of a turn (64.6 2b-ii)
  flightEnd: 'host', capture: 'host', buildDrill: 'host',
};

/** A presenter that shows nothing: for applying inputs where nobody watches (tests, replays, the harness). */
export const SILENT_PRESENTER: SimPresenter = {
  holdFlightEnd: () => false, flightEndReached: () => {}, launched: () => {}, probeScan: () => {},
  geyserDeflect: () => {}, impacted: () => {}, lost: () => {}, flightOver: () => {}, airborne: () => {},
};

const refuse = (reason: string, code: string) => ({ ok: false as const, reason, code });

/** Apply one input body to the match through today's methods. Returns the method's own result. */
export function dispatchInput(m: Match, actor: number, body: MatchInputBody, p: SimPresenter): PlanResult<object> {
  switch (body.kind) {
    case 'turn': return dispatchTurn(m, actor, body.input, p);
    case 'intent': {
      // 64.10 (lane C): only the seat that holds a unit drives it; on the grid (Match.setIntent), its turn an impulse
      const u = m.state.players.find(q => q.id === body.unitId);
      if (!u) return refuse('No such unit', 'unit');
      if (m.state.controllers?.[u.id] !== actor) return refuse('The sending seat does not hold that unit', 'held');
      m.setIntent(body.unitId, body.intent);
      return { ok: true };
    }
    case 'possess': {
      const team = seatTeam(actor);
      if (team === null) return refuse('Only a seat possesses', 'actor');
      if (body.target && 'seat' in body.target) {
        // 64.11: taking a seat is taking whoever sits in it (decision 51: one switch for units and seats); leaving it,
        // their brain resumes. One seat per emplacement for now (R12's seats[] later); an empty seat has no one to take
        const { structureId, seat } = body.target.seat;
        if (m.state.config.clock !== 'action') return refuse('Seats are taken on the action clock', 'clock');
        const st = m.state.structures.find(x => x.id === structureId);
        const target = st ? seatAt(st, seat) : undefined;
        if (!target) return refuse('No such seat', 'seat');
        const sitting = seatUnit(target);
        if (sitting === null) return refuse('That seat is empty: walk someone to it', 'empty');
        return m.takeUnit(actor, team, sitting);
      }
      return m.takeUnit(actor, team, body.target ? body.target.unit : null);
    }
    case 'decision': {
      // R11 step 6 / 64.11: a brain decides for its own unit (the actor is its id); a held unit's brain is quiet
      if (seatTeam(actor) !== null || actor !== body.unitId) return refuse('A brain decides for its own unit', 'actor');
      const u = m.state.players.find(q => q.id === body.unitId);
      if (!u || !u.alive) return refuse('No such unit', 'unit');
      if (m.state.controllers?.[u.id] !== undefined) return refuse('A held unit follows its holder', 'held');
      return m.setGunDecision(u, body.decision);
    }
    case 'toggle':
      return body.toggle === 'holdFire' ? m.toggleHoldFire(body.unitId) : m.cancelOrder(body.unitId);
    case 'throw': {
      // 65.15: a grenade thrown — by the unit's own brain, or the seat that holds it
      const u = m.state.players.find(q => q.id === body.unitId);
      if (!u) return refuse('No such unit', 'unit');
      if (seatTeam(actor) !== null && m.state.controllers?.[u.id] !== actor) return refuse('The sending seat does not hold that unit', 'held');
      if (seatTeam(actor) === null && actor !== u.id) return refuse('A brain decides for its own unit', 'actor');
      return m.throwGrenade(u, body.grenade, body.dir);
    }
    case 'charge': {
      // 69.47 step 1: an engineer's charge placed or its wire pulled — by the unit's own brain, or the seat that holds it
      const u = m.state.players.find(q => q.id === body.unitId);
      if (!u) return refuse('No such unit', 'unit');
      if (seatTeam(actor) !== null && m.state.controllers?.[u.id] !== actor) return refuse('The sending seat does not hold that unit', 'held');
      if (seatTeam(actor) === null && actor !== u.id) return refuse('A brain decides for its own unit', 'actor');
      return body.step === 'place' ? m.placeCharge(u, body.fuse ?? 'timer') : m.blowCharges(u);
    }
    case 'vehicleSmoke': {
      // 63.1u: the driver pops its vehicle's smoke — the unit's own brain, or the seat that holds it
      const u = m.state.players.find(q => q.id === body.unitId);
      if (!u) return refuse('No such unit', 'unit');
      if (seatTeam(actor) !== null && m.state.controllers?.[u.id] !== actor) return refuse('The sending seat does not hold that unit', 'held');
      if (seatTeam(actor) === null && actor !== u.id) return refuse('A brain decides for its own unit', 'actor');
      return m.popVehicleSmoke(u, body.toward);
    }
    case 'drone': {
      const u = m.state.players.find(q => q.id === body.unitId);
      if (!u) return refuse('No such unit', 'unit');
      if (seatTeam(actor) !== null && m.state.controllers?.[u.id] !== actor) return refuse('The sending seat does not hold that unit', 'held');
      if (body.step === 'task') return m.beginDroneTasking(u);
      if (body.step === 'cancel') return m.cancelDroneTasking(u);
      if (body.step === 'strike') return m.strikeDrone(u, body.strike);   // 65.16
      return m.launchDrone(u, body.route ?? [], body.strike, body.payload);
    }
    case 'pace': return { ok: true };   // recorded (111); speed never changes a tick
    case 'order': {
      // lane D: a seat of the team, or one of its units (a commander brain), may order its squads
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may order its squads', 'actor');
      return m.order(body);
    }
    // lane D: recorded as a switch (31, 111); the sector keeps the switch schedule on its running record, the Match logs that it happened
    case 'fidelity': return { ok: true };
    case 'efforts': {
      // §0.11 step D: the side's efforts — a seat of the team, or one of its units (a commander brain), as orders are
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may set its efforts', 'actor');
      return m.setEfforts(body);
    }
    case 'coordinate': {
      // 68.22 (lane D): a play — a seat of the team, or one of its units (a commander brain), as orders are
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may order its squads', 'actor');
      return m.coordinate(body);
    }
    case 'respawnAt': {
      // 69.6h (lane D): a human seat's respawn pick — the seat's own, never a brain's
      const team = seatTeam(actor);
      if (team === null) return refuse('Only a player picks where it comes back', 'actor');
      return m.setRespawnPick(actor, team, body);
    }
    case 'vehicles': {
      // 68.25 (lane D): the team's standing order on vehicles — a seat of the team, or one of its units (a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may order its vehicles', 'actor');
      return m.setVehicleUse(body);
    }
    case 'motorPool': {
      // 63.1n with 67.75: the side's motor pool — its standing order, an unpack by hand — a seat of the team, or one of its units (a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may order its motor pool', 'actor');
      return m.orderMotorPool(body);
    }
    case 'spawnAt': {
      // DECISIONS 200 step 3: the side's standing order on where its waves land — a seat of the team, or one of its units (a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may say where its waves land', 'actor');
      return m.setSpawnAt(body);
    }
    case 'reserves': {
      // 68.4 (lane D): the team's reserve — a seat of the team, or one of its units (a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its reserve', 'actor');
      return m.callReserves(body);
    }
    case 'mortarCall': {
      // 69.23: a call for the side's mortar — its seat (a human squad leader's radial) or one of its units (a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its mortar', 'actor');
      return m.callMortar(body);
    }
    case 'airstrike': {
      // 68.39: the side's airstrike — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its airstrikes', 'actor');
      return m.callAirstrike(body);
    }
    case 'lighter': {
      // 69.10: the side's lighter — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its lighters', 'actor');
      return m.callLighter(body, body.by ?? (team !== null ? 'human' : 'commander'));
    }
    case 'dropship': {
      // 69.3b: the side's dropship — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its dropships', 'actor');
      return m.callDropship(body, body.by ?? (team !== null ? 'human' : 'commander'));
    }
    case 'pickup': {
      // 69.18: the side's wave ship sent to fetch a squad home — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may send its ship', 'actor');
      return m.callPickup(body, body.by ?? (team !== null ? 'human' : 'commander'));
    }
    case 'rescue': {
      // 69.62: the side's hot rescue — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its rescue', 'actor');
      return m.callRescue(body, body.by ?? (team !== null ? 'human' : 'commander'));
    }
    case 'supplyDrop': {
      // 69.32 step 2: the side's supply drone called in — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its drones', 'actor');
      return m.callSupplyDrop(body);
    }
    case 'podDrop': {
      // DECISIONS 200 step 5: the side's pod drop — its commander's seat (or one of its units: a commander brain)
      const team = seatTeam(actor);
      const unitTeam = team === null ? m.state.players.find(u => u.id === actor)?.teamIndex : undefined;
      if ((team ?? unitTeam) !== body.team) return refuse('Only the team may call its pod drops', 'actor');
      return m.callPods(body, body.by ?? (team !== null ? 'human' : 'commander'));
    }
  }
}

function dispatchTurn(m: Match, actor: number, input: TurnInput, p: SimPresenter): PlanResult<object> {
  const player = m.state.players.find(q => q.id === input.playerId);
  if (!player) return refuse('No such unit', 'unit');
  const prm = input.params;
  switch (input.action) {
    case 'fire': return m.fire(player, prm as FireParams, p);
    case 'queue':
      if (m.roundFlow) {
        // a brain's queue is the bot host's prepaid order, its target remembered for the shot record
        if (seatTeam(actor) === null) return m.queueBotShot(player, prm as FireParams);
        const queued = { ok: true as const, projectile: m.queueRoundOrder(player, prm as FireParams) };
        return queued;
      }
      m.queueBatchOrder(player, prm as FireParams);
      return { ok: true };
    case 'unqueue': {
      const q = m.unqueue(player.id);
      if (!q) return refuse('Nothing queued', 'unqueue');
      const queued = { ok: true as const, order: q };
      return queued;
    }
    case 'jump':
    case 'jetpack': return m.jump(player, prm as JetpackParams);
    case 'move': return m.move(player, (prm as MoveParams).surfaceDir);
    case 'shop': {
      const r = m.shop(player, prm as ShopParams);
      return r.ok ? r : refuse('', r.reason);
    }
    case 'build': { const b = prm as BuildParams; return m.build(player, b.structureType, b.surfaceDir); }
    case 'mount': return m.mount(player, (prm as MountParams).structureId);
    case 'dismount': return m.dismount(player);
    case 'heal': return m.heal(player, (prm as HealParams).itemKey);
    case 'melee': return m.melee(player, (prm as MeleeParams).targetId);
    case 'plantFlag': return m.plantStake(player, (prm as PlantFlagParams).surfaceDir);
    case 'removeStake': return m.removeStake(player, (prm as RemoveStakeParams).stakeId);
    case 'pickup': return m.pickup(player, (prm as PickupParams).objectId);
    case 'drop': return m.drop(player, (prm as DropParams).carryableId);
    case 'interact': return m.interact(player, (prm as RuinParams).ruinId);
    case 'claim': {
      const id = (prm as RuinParams).ruinId;
      return id === undefined ? refuse('Claim which ruin?', 'claim') : m.claim(player, id);
    }
    case 'collect': return m.collect(player, prm as CollectActionParams);
    case 'deliver': return m.deliver(player, (prm as DropParams).carryableId);
    case 'command': return m.command(player, prm as CommandParams);
    case 'detonate': return m.detonateAtStep((prm as DetonateParams).step);
    case 'skip': return m.endTurnBy(actor, player, p);   // 64.6 2b-ii: the end of a turn is an input
    case 'flightEnd': case 'capture': case 'buildDrill':
      return refuse(`'${input.action}' is driven by the host until R11 step 2`, 'host');
  }
}
